import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCart } from '../App';
import { CheckCircle, Package, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SMILEY_LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_121ad1ad-d724-4658-ab95-7dc4ce7b4a83/artifacts/766c9fb51cb57f3d_image.png";

const OrderConfirmation = () => {
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [pollCount, setPollCount] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    if (!orderId && !sessionId) {
      setPaymentStatus('error');
      return;
    }

    const checkPayment = async () => {
      try {
        if (sessionId) {
          const statusRes = await fetch(`${API}/checkout/status/${sessionId}`);
          const statusData = await statusRes.json();
          if (statusData.payment_status === 'paid') {
            setPaymentStatus('success');
            clearCart();
            if (orderId) {
              const orderRes = await fetch(`${API}/orders/${orderId}`);
              if (orderRes.ok) setOrder(await orderRes.json());
            }
            return;
          }
        }
        
        if (orderId) {
          const orderRes = await fetch(`${API}/orders/${orderId}`);
          if (orderRes.ok) {
            const orderData = await orderRes.json();
            setOrder(orderData);
            if (orderData.payment_status === 'paid') {
              setPaymentStatus('success');
              clearCart();
              return;
            }
          }
        }

        if (pollCount < 5) {
          setTimeout(() => setPollCount(p => p + 1), 2000);
        } else {
          setPaymentStatus('pending');
        }
      } catch (error) {
        if (pollCount < 5) {
          setTimeout(() => setPollCount(p => p + 1), 2000);
        } else {
          setPaymentStatus('error');
        }
      }
    };

    checkPayment();
  }, [orderId, sessionId, pollCount, clearCart]);

  if (paymentStatus === 'checking') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="payment-checking">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-6 border-2 border-cs-ochre border-t-transparent animate-spin" />
          <h1 className="font-anton text-3xl uppercase text-cs-text mb-2">Verifying Payment</h1>
          <p className="font-mono text-sm text-cs-muted">Please wait while we confirm your order.</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'error') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="payment-error">
        <div className="text-center max-w-md px-4">
          <div className="w-20 h-20 mx-auto mb-6 bg-cs-alert flex items-center justify-center">
            <span className="font-anton text-4xl text-cs-text">!</span>
          </div>
          <h1 className="font-anton text-4xl uppercase text-cs-text mb-4">Payment Issue</h1>
          <p className="font-mono text-sm text-cs-muted mb-6">
            There was an issue processing your payment. Please try again or contact support.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/cart" className="bg-cs-olive text-cs-text font-mono uppercase tracking-wider text-sm px-6 py-3 hover:bg-cs-olive2 transition-colors" data-testid="try-again">
              Try Again
            </Link>
            <Link to="/contact" className="border border-cs-border text-cs-muted font-mono uppercase tracking-wider text-sm px-6 py-3 hover:border-cs-ochre hover:text-cs-ochre transition-colors" data-testid="contact-support">
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in" data-testid="order-confirmation-page">
      {/* Success Header */}
      <section className="bg-cs-olive py-16 border-b border-cs-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-24 h-24 mx-auto mb-6 bg-cs-dark border border-cs-border flex items-center justify-center">
            <CheckCircle className="w-14 h-14 text-cs-ochre" />
          </div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase text-cs-text mb-4" data-testid="success-title">
            Thank You!
          </h1>
          <p className="font-mono text-sm text-cs-text/80">
            Your order has been placed successfully. Have the day you deserve!
          </p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {order && (
          <div className="bg-cs-card border border-cs-border p-6 mb-8" data-testid="order-details">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-anton text-2xl uppercase text-cs-text">Order Details</h2>
              <span className="font-mono text-xs bg-cs-olive text-cs-text px-3 py-1 uppercase tracking-wider">
                {order.payment_status === 'paid' ? 'Paid' : 'Processing'}
              </span>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div>
                <p className="font-mono text-xs text-cs-muted uppercase tracking-wider">Order ID</p>
                <p className="font-mono text-sm text-cs-text font-bold">{order.id.slice(0, 8).toUpperCase()}</p>
              </div>
              <div>
                <p className="font-mono text-xs text-cs-muted uppercase tracking-wider">Email</p>
                <p className="font-mono text-sm text-cs-text">{order.email}</p>
              </div>
              <div>
                <p className="font-mono text-xs text-cs-muted uppercase tracking-wider">Shipping To</p>
                <p className="font-mono text-sm text-cs-text">
                  {order.shipping_address?.firstName} {order.shipping_address?.lastName}<br />
                  {order.shipping_address?.address}<br />
                  {order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.zip}
                </p>
              </div>
              <div>
                <p className="font-mono text-xs text-cs-muted uppercase tracking-wider">Est. Delivery</p>
                <p className="font-mono text-sm text-cs-text">5-7 Business Days</p>
              </div>
            </div>

            <div className="border-t border-cs-border pt-6">
              <h3 className="font-anton text-lg uppercase text-cs-text mb-4">Items</h3>
              <div className="space-y-4">
                {order.items.map((item) => (
                  <div key={`${item.product_id}-${item.size}-${item.color}`} className="flex gap-4 items-center">
                    <img src={item.image} alt={item.name} className="w-16 h-16 object-cover border border-cs-border" />
                    <div className="flex-1">
                      <p className="font-mono text-sm text-cs-text font-bold uppercase">{item.name}</p>
                      <p className="font-mono text-xs text-cs-muted">{item.size} / {item.color} x {item.quantity}</p>
                    </div>
                    <p className="font-mono text-sm text-cs-ochre font-bold">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-cs-border mt-6 pt-6 space-y-2 font-mono text-sm">
              <div className="flex justify-between"><span className="text-cs-muted">Subtotal</span><span className="text-cs-text">${order.subtotal?.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-cs-muted">Shipping</span><span className="text-cs-text">{order.shipping === 0 ? 'FREE' : `$${order.shipping?.toFixed(2)}`}</span></div>
              <div className="flex justify-between"><span className="text-cs-muted">Tax</span><span className="text-cs-text">${order.tax?.toFixed(2)}</span></div>
              {order.discount > 0 && <div className="flex justify-between text-green-500"><span>Discount</span><span>-${order.discount?.toFixed(2)}</span></div>}
              <div className="flex justify-between pt-4 border-t border-cs-border">
                <span className="font-anton text-xl uppercase text-cs-text">Total</span>
                <span className="text-2xl text-cs-ochre font-bold">${order.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="bg-cs-card border border-cs-border p-6 mb-8">
          <h2 className="font-anton text-xl uppercase text-cs-text mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-cs-ochre" /> What's Next?
          </h2>
          <ul className="space-y-3 font-mono text-sm">
            <li className="flex items-start gap-3"><span className="w-6 h-6 bg-cs-olive text-cs-text flex items-center justify-center flex-shrink-0 text-xs">1</span><span className="text-cs-muted">You'll receive an email confirmation shortly.</span></li>
            <li className="flex items-start gap-3"><span className="w-6 h-6 bg-cs-olive text-cs-text flex items-center justify-center flex-shrink-0 text-xs">2</span><span className="text-cs-muted">We'll send tracking info once your order ships.</span></li>
            <li className="flex items-start gap-3"><span className="w-6 h-6 bg-cs-olive text-cs-text flex items-center justify-center flex-shrink-0 text-xs">3</span><span className="text-cs-muted">Rock your new gear and tag us @crookidsmilez!</span></li>
          </ul>
        </div>

        <div className="text-center">
          <img src={SMILEY_LOGO} alt="Crookid Smilez" className="w-20 h-20 mx-auto mb-4" />
          <p className="font-mono text-sm text-cs-muted mb-6">Thanks for joining the Crookid Smilez family!</p>
          <Link to="/shop" className="bg-cs-olive text-cs-text font-mono uppercase tracking-wider text-sm px-8 py-4 hover:bg-cs-olive2 transition-colors inline-flex items-center gap-2" data-testid="continue-shopping">
            Continue Shopping <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
